import { Alert, Button, Checkbox, Flex, Segmented, Table, Typography } from 'antd';
import { FC, useMemo, useState } from 'react';

import { ReloadOutlined } from '@ant-design/icons';
import { type IncorporationCompany, useIncorporations } from '@hooks/api';
import { getApiErrorMessage } from '@utils';

import IncorporationDrawer from './IncorporationDrawer';
import { columns, provisionedColumns } from './columns';
import * as S from './styled';

const { Text } = Typography;

/**
 * The CSM handover point: companies Master Data is still incorporating, and the one action that
 * brings a chosen one into the legacy platform. Titled "Migrations" — the route, the component and
 * MDS's own vocabulary stay 'incorporation', which is what the data is called on both sides of the
 * wire; only what the CSMs read changed.
 *
 * The list is read live from Master Data on every request rather than mirrored here — MDS owns the
 * record, and a copy would be one more thing to keep in step with it. That is also why the search
 * and the paging are MDS's own (`?search=`, `?page=`): the queue is whatever MDS says it is, forty
 * rows at a time.
 *
 * The "Not provisioned" filter is the exception, and it has to be. Whether legacy already holds a
 * company is decided by *our* backend against the legacy database after MDS has answered — MDS's
 * company list takes `page` and `search` and nothing else — so the filter cannot be pushed down to
 * where the paging happens. Filtering only the loaded page would be worse than useless here: most of
 * `incorporating` is existing clients the legacy sync mirrored into MDS (~81 on production), so a
 * page of forty can hold a handful of unprovisioned rows while the paginator still reports MDS's
 * full count — and a CSM would read that handful as the whole queue. Turning it on therefore reads
 * every page first (`all`) and pages the filtered result locally.
 *
 * Admin-only, in three places that have to agree: the route's `roles` in `getPaths.tsx`, the
 * sidebar item's explicit `roles`, and `@AdminRoles(AdminRole.Admin)` on the backend controller.
 * The API is the one that actually enforces it; the other two exist so nobody is shown a door that
 * opens onto Access denied [ENG-1197].
 */
const Incorporations: FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  /**
   * Which list this is. Three, not a boolean, because the third one is a different query:
   * `queue` and `unprovisioned` are two readings of what MDS still calls `incorporating`,
   * while `provisioned` asks the backend for the companies this page has already taken into
   * service — which have left that status and cannot be reached from the other two at all.
   */
  const [view, setView] = useState<'queue' | 'unprovisioned' | 'provisioned'>('queue');
  const onlyUnprovisioned = view === 'unprovisioned';
  /**
   * In "Needs refresh", whether to keep only what Master Data touched since the last fill. On by
   * default — that is the work list — and off for the company whose change `last_activity_at` did
   * not count, which would otherwise have no way back to its Refresh button.
   */
  const [changedOnly, setChangedOnly] = useState(true);
  const [selected, setSelected] = useState<IncorporationCompany | null>(null);

  const { data, isPending, error, refetch, isFetching } = useIncorporations({
    page,
    search,
    all: onlyUnprovisioned,
    provisioned: view === 'provisioned',
    ...(view === 'provisioned' ? { changedOnly } : {}),
  });

  const perPage = data?.perPage ?? 40;

  const matching = useMemo(() => {
    const records = data?.records ?? [];

    // On `legacyMatchedBy`, which is the field the Legacy column's three states branch on, so this
    // is exactly the set it tags "Not provisioned". The backend writes it with `legacyCompanyId` and
    // filtering on that would agree today — but only for as long as the two are written together,
    // which is an invariant on the other side of the wire that nothing here would catch breaking.
    return onlyUnprovisioned
      ? records.filter((record) => record.legacyMatchedBy === null)
      : records;
  }, [data?.records, onlyUnprovisioned]);

  /*
   * The filtered view pages locally, so the local `page` is the current one; unfiltered, the page MDS
   * actually served is (`fetchEveryPage` always reports 1, which would pin the paginator to it).
   *
   * Not clamped here: provisioning a company drops it out of this filter, so the last page can stop
   * existing while the CSM is standing on it — but Ant Design's own `usePagination` already pulls
   * `current` back to the last page that exists when `total` shrinks under it.
   */
  const currentPage = onlyUnprovisioned ? page : (data?.page ?? page);

  /*
   * Not sliced here: Ant Design's Table pages `dataSource` itself whenever its length reaches
   * `pagination.total`, which the filtered view always does — `total` is that length. The unfiltered
   * view takes the other side of the same branch (a served page shorter than MDS's total is rendered
   * as-is), which is how this page already worked. Slicing first would just do it twice.
   */
  const rows = useMemo(
    () =>
      matching.map((record) => ({
        ...record,
        onOpen: () => setSelected(record),
      })),
    [matching]
  );

  return (
    <Flex vertical gap={16}>
      <Flex align="center" justify="space-between" gap={16} wrap>
        <S.Heading level={4}>Migrations</S.Heading>

        <Flex align="center" gap={8}>
          <Segmented
            value={view}
            // Back to the first page on every switch: the views have different lengths, and page 3
            // of the queue is rarely page 3 of the handful inside it.
            onChange={(value) => {
              setView(value as 'queue' | 'unprovisioned' | 'provisioned');
              setPage(1);
            }}
            options={[
              { label: 'All', value: 'queue' },
              { label: 'Not provisioned', value: 'unprovisioned' },
              {
                label: 'Needs refresh',
                value: 'provisioned',
                title:
                  'Companies this page provisioned whose Master Data has changed since the last fill — ' +
                  'press Refresh from Master Data on these',
              },
            ]}
          />
          {view === 'provisioned' && (
            <Checkbox
              checked={changedOnly}
              onChange={(event) => {
                setChangedOnly(event.target.checked);
                setPage(1);
              }}
            >
              Only changed in Master Data
            </Checkbox>
          )}
          <S.SearchInput
            allowClear
            placeholder="Legal name, trade name, RCS, VAT"

            // Searched on submit, not per keystroke: each one is a round trip to Master Data.
            onSearch={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Flex>
      </Flex>

      {data?.truncated && (
        <Alert
          type="warning"
          showIcon
          message={
            view === 'provisioned' ? 'This is not the whole answer' : 'This is not the whole queue'
          }
          description={
            view === 'provisioned'
              ? // The backend checks the newest few hundred provisioned companies against Master
                // Data and stops. Older ones are unchecked rather than known to be quiet, and a CSM
                // reading this list as exhaustive is the failure worth preventing.
                'Only the most recently provisioned companies were checked against Master Data. Narrow it with the search to reach an older one.'
              : `The filter reads the queue a page at a time and stopped after ${data.records.length} ` +
                'companies. Narrow it with the search, or provision what is here and refresh.'
          }
        />
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          message="Could not read the incorporation queue"
          description={getApiErrorMessage(
            error,
            'Master Data did not answer. It is reachable in-cluster and over the VPN only.'
          )}
        />
      )}

      <S.QueueContainer>
        <Table
          rowKey="mdsCompanyId"
          loading={isPending}
          // `onOpen` per row so the trailing Open button — the keyboard-reachable way in — can raise
          // the same drawer the row click does.
          dataSource={rows}
          columns={view === 'provisioned' ? provisionedColumns : columns}
          onRow={(row) => ({ onClick: () => setSelected(row) })}
          pagination={{
            current: currentPage,
            // Master Data pins its own page size and exposes no way to change it, so this is reported
            // rather than chosen — showSizeChanger would offer a control that does nothing.
            pageSize: perPage,
            // The filtered count, not MDS's: reporting MDS's total beside a filtered page is the
            // thing that would let a CSM mistake a handful of rows for the whole queue.
            total: onlyUnprovisioned ? matching.length : (data?.total ?? 0),
            showSizeChanger: false,
            onChange: setPage,
          }}
          locale={{
            // Not "no companies" when the request failed: `data` is undefined either way, and an empty
            // state under an error banner tells the CSM the queue is clear when nobody knows if it is.
            emptyText: error ? (
              <Text type="secondary">The queue could not be read.</Text>
            ) : view === 'provisioned' && !changedOnly ? (
              // Neutral on purpose: a search that matches nothing, and companies Master Data could not
              // serve, come back just as empty as a page that has provisioned nobody.
              <Text type="secondary">No company this page provisioned could be listed.</Text>
            ) : view === 'provisioned' ? (
              // The backend filters before it pages, so an empty list here means there is nothing to
              // refresh — not that this page of a longer list happened to be quiet.
              <Text type="secondary">
                Nothing this page provisioned has changed in Master Data since it was last filled.
              </Text>
            ) : onlyUnprovisioned ? (
              <Text type="secondary">
                Nothing in the queue is waiting to be provisioned.
              </Text>
            ) : (
              <Text type="secondary">
                No company is currently being incorporated.
              </Text>
            ),
          }}
        />
      </S.QueueContainer>

      <IncorporationDrawer
        company={selected}
        onClose={() => setSelected(null)}
      />
    </Flex>
  );
};

export default Incorporations;
